/**
 * Barra horizontal (ECharts) para chart-bar dentro de charts_group.
 * Crece con animación al entrar en el viewport (IntersectionObserver).
 */

const BAR_GROW_MS = 900;

export default class ChartBar {
	private root: HTMLElement;
	private plot: HTMLElement | null = null;
	private chart: any = null;
	private resizeObserver: ResizeObserver | null = null;
	private viewportObserver: IntersectionObserver | null = null;
	private revealed = false;
	private targetValue: number | null = null;
	private axisStart: number | null = null;
	private valuesRevealTimer: number | null = null;

	constructor(root: HTMLElement) {
		this.root = root;
	}

	private getBarWidthPx(): number {
		if (!this.plot) return 48;
		const h =
			this.plot.clientHeight || Math.round(this.plot.getBoundingClientRect().height);
		return Math.max(1, h || 48);
	}

	private buildSeriesBase(
		value: number,
		barColor: string,
		borderWidth: number,
		borderColor: string,
		barWidthPx: number
	) {
		return {
			type: 'bar' as const,
			data: [value],
			barWidth: barWidthPx,
			itemStyle: {
				color: barColor,
				borderColor,
				borderWidth,
			},
			emphasis: { disabled: true },
			silent: true,
		};
	}

	private armTailValuesReveal(animated: boolean) {
		if (!this.root.querySelector('.hg-chart-bar__value--tail')) return;

		if (this.valuesRevealTimer !== null) {
			clearTimeout(this.valuesRevealTimer);
			this.valuesRevealTimer = null;
		}

		const show = () => {
			this.valuesRevealTimer = null;
			this.root.classList.add('is-chart-values-visible');
		};

		if (!animated) {
			requestAnimationFrame(show);
			return;
		}

		this.valuesRevealTimer = window.setTimeout(show, BAR_GROW_MS);
	}

	private reveal() {
		if (this.revealed || !this.chart || this.targetValue === null) return;
		this.revealed = true;
		this.viewportObserver?.disconnect();
		this.viewportObserver = null;

		const v = this.targetValue;
		const reduceMotion =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

		const animated = !reduceMotion && v !== this.axisStart;

		this.chart.setOption(
			{
				animation: !reduceMotion,
				animationDuration: reduceMotion ? 0 : BAR_GROW_MS,
				animationDurationUpdate: reduceMotion ? 0 : BAR_GROW_MS,
				animationEasing: 'cubicOut',
				animationEasingUpdate: 'cubicOut',
				series: [this.buildSeriesBase(v, ...this.getColors(), this.getBarWidthPx())],
			},
			{ lazyUpdate: false }
		);

		this.armTailValuesReveal(animated);
	}

	private getColors(): [string, number, string] {
		if (!this.plot) return ['transparent', 0, 'transparent'];
		const barColor = (this.plot.dataset.barColor ?? '').trim() || 'transparent';
		const borderRaw = (this.plot.dataset.borderColor ?? '').trim();
		const borderWidth = borderRaw !== '' ? 1 : 0;
		const borderColor = borderRaw !== '' ? borderRaw : 'transparent';
		return [barColor, borderWidth, borderColor];
	}

	init() {
		const plot = this.root.querySelector<HTMLElement>('.hg-chart-bar__plot[data-chart-bar]');
		if (!plot) return;

		const EC = window.echarts;
		if (!EC) return;

		this.plot = plot;

		const min = parseFloat(plot.dataset.min ?? '');
		const max = parseFloat(plot.dataset.max ?? '');
		let v = parseFloat(plot.dataset.value ?? '');
		if (!Number.isFinite(min) || !Number.isFinite(max) || !Number.isFinite(v)) return;

		const lo = Math.min(min, max);
		const hi = Math.max(min, max);
		v = Math.min(Math.max(v, lo), hi);

		let baseline = lo;
		if (lo < 0 && hi > 0) {
			baseline = 0;
		} else if (hi <= 0) {
			baseline = hi;
		} else {
			baseline = lo;
		}
		baseline = Math.min(Math.max(baseline, lo), hi);

		const [barColor, borderWidth, borderColor] = this.getColors();
		const startValue = baseline;

		this.targetValue = v;

		this.root.classList.remove('is-chart-values-visible');
		this.axisStart = baseline;

		this.chart = EC.init(plot, null, { renderer: 'svg' });

		this.chart.setOption({
			animation: false,
			grid: { left: 0, right: 0, top: 0, bottom: 0, containLabel: false },
			xAxis: {
				type: 'value',
				min: lo,
				max: hi,
				show: false,
			},
			yAxis: {
				type: 'category',
				data: [''],
				show: false,
			},
			series: [
				this.buildSeriesBase(startValue, barColor, borderWidth, borderColor, this.getBarWidthPx()),
			],
		});

		this.resizeObserver = new ResizeObserver(() => {
			if (!this.chart || !this.plot) return;
			this.chart.resize();
			const bw = this.getBarWidthPx();
			this.chart.setOption({ series: [{ barWidth: bw }] });
		});
		this.resizeObserver.observe(plot);

		const reduceMotion =
			typeof window !== 'undefined' &&
			window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

		if (reduceMotion || v === startValue) {
			this.reveal();
			return;
		}

		this.viewportObserver = new IntersectionObserver(
			(entries) => {
				if (entries.some((e) => e.isIntersecting)) {
					this.reveal();
				}
			},
			{
				root: null,
				threshold: 0.12,
				rootMargin: '0px 0px 0px 0px',
			}
		);
		this.viewportObserver.observe(this.root);

		requestAnimationFrame(() => {
			if (this.revealed) return;
			const r = this.root.getBoundingClientRect();
			const vh = window.visualViewport?.height ?? window.innerHeight;
			if (r.top < vh && r.bottom > 0 && r.left < window.innerWidth && r.right > 0) {
				this.reveal();
			}
		});
	}

	cleanup() {
		this.viewportObserver?.disconnect();
		this.viewportObserver = null;
		if (this.valuesRevealTimer !== null) {
			clearTimeout(this.valuesRevealTimer);
			this.valuesRevealTimer = null;
		}
		this.root.classList.remove('is-chart-values-visible');
		this.resizeObserver?.disconnect();
		this.resizeObserver = null;
		this.chart?.dispose();
		this.chart = null;
		this.plot = null;
		this.revealed = false;
		this.targetValue = null;
		this.axisStart = null;
	}
}
